/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { TDictionary } from '../../../util/types';
import { Dimension, Product } from '../../products/storage/types';

export enum DeliveryMode {
  Delivery = 'Delivery',
  Pickup = 'Pickup',
  WarePickup = 'WarePickup',
  Card = 'Card',
  None = '',
}

export const deliveryTypeMap: TDictionary<DeliveryMode> = {
  Pickup: DeliveryMode.Pickup,
  Card: DeliveryMode.Card,
  CarryOut: DeliveryMode.Pickup,
  WarePickup: DeliveryMode.WarePickup,
  // Only blank is explicity none
  '': DeliveryMode.None,
  // All other delivery modes map to a type of delivery
  undefined: DeliveryMode.Delivery,
};

export type DeliveryModeType = keyof typeof DeliveryMode;

export enum OrderStatus {
  AtDirectShipper = 'AtDirectShipper',
  Created = 'Created',
  OpenOrder = 'OpenOrder',
  OpenLine = 'OpenLine',
  InProcessing = 'InProcessing',
  Backordered = 'Backordered',
  PartiallyPicked = 'PartiallyPicked',
  Pending = 'Pending',
  Picked = 'Picked',
  Delivered = 'Delivered',
  Invoiced = 'Invoiced',
  PartiallyDelivered = 'PartiallyDelivered',
  PartiallyInvoiced = 'PartiallyInvoiced',
  PartiallyExchanged = 'PartiallyExchanged',
  ToBeDirectDelivered = 'ToBeDirectDelivered',
  CustomerCanceled = 'CustomerCanceled',
  SystemCanceled = 'SystemCanceled',
  Canceled = 'Canceled',
  Returned = 'Returned',
  OnHold = 'OnHold',
  FraudHold = 'FraudHold',
}

export type OrderStatusType = keyof typeof OrderStatus;

export interface SearchSalesOrdersPayload {
  salesId?: string;
  // "confirmationId": "67CB96ARYANL",
  accountNumber: string;
  skip: number;
  top: number;
  startDateTime?: Date;
  endDateTime?: Date;
  deliveryMode?: 'Pickup' | 'Card' | '!Card' | 'WarePickup';
}

export interface SearchSalesOrdersResponse {
  salesOrders: SalesOrder[];
}

export interface SalesLine {
  productId: number;
  quantity: number;
  quantityRemained: number;
  description: string;
  netAmount: number;
  baseUnitPrice: number;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
  detailedLineStatus: OrderStatusType;
  inventoryLocationId?: string;
  inventoryTransactionId?: string;
  isPriceOverridden?: boolean;
  itemId: string;
  imageUrl: string;
  originalPriceExcGst?: number;
  productNumber: string;
  productDimensions?: Dimension[];
}

interface ShippingAddressType {
  fullAddress: string;
  name: string;
  recordId: number;
}

export interface SalesOrder {
  salesId: string;
  confirmationId: string;
  accountNumber: string;
  deliveryMode: string;
  detailedStatus: OrderStatusType;
  customerReference?: string;
  notes?: string;
  deliveryInstructions?: string;
  dairySupplyNumber?: string;
  internalNotes?: string;
  cardVendor?: string;
  createdAt: Date;
  totalPrice: number;
  shippingAddress?: ShippingAddressType;
  shippingAmount: number;
  totalAmountIncGst: number;
  gstAmount: number;
  discountIncGst: number;
  discountExcGst: number;
  shippingAmountIncGst: number;
  shippingAmountExcGst: number;
  salesLines: SalesLine[];
}

// Same as past products but we don't care about discount price just price the user paid for the quantity.
export interface SalesOrderProductLine extends Product {
  pricePaid: number;
}

// `sales-orders-details?accountNumber={accountNumber}&orderId={orderId}`
export interface SalesOrderDetails extends SalesOrder {
  deliveryName: string;
  deliveryAddress: string;
  trackingCode?: string;
  notes: string;
  productLines: SalesOrderProductLine[];
  // Null if mode = 'pickup'
  deliveryFee: number | null;
}

export interface GetOrderRuducerAction {
  data: SalesOrder;
  orderId: string;
}
export interface SalesOrderReducerAction {
  data: SalesOrder[];
  accountNumber: string;
  last?: boolean;
}

export type PastOrdersList = SalesOrder[];

export interface OrdersRequestCache {
  status: 'pending' | 'fulfilled' | 'rejected';
  lastRequest: SearchSalesOrdersPayload;
  last?: boolean;
  hash: string;
}

export type GetOrderStatusType = 'pending' | 'fulfilled' | 'rejected' | null;

export interface OrdersState {
  // Indexed by [Account Number] then [Query Hash]
  salesOrders: TDictionary<TDictionary<SalesOrder[]>>;
  salesStatus: TDictionary<TDictionary<OrdersRequestCache>>;

  salesOrder: SalesOrder | undefined;
  orderStatus: GetOrderStatusType;
}
